import type { CSSProperties } from 'react'
import {
  Radar,
  RadarChart as RechartsRadar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Legend,
  ResponsiveContainer,
} from 'recharts'
import { COLORS } from '../../styles/tokens'

interface RadarChartProps {
  currentData:  Record<string, number>   // category → 0-100
  lastWeekData: Record<string, number>   // category → 0-100, may be empty
  labels:       Record<string, string>   // category → display label
  currentLabel: string
  lastLabel:    string
  ariaLabel?:   string
}

export default function RadarChart({
  currentData,
  lastWeekData,
  labels,
  currentLabel,
  lastLabel,
  ariaLabel,
}: RadarChartProps) {
  const categories = Object.keys(currentData)
  if (categories.length === 0) return null

  const hasLastWeek = Object.keys(lastWeekData).length > 0

  const chartData = categories.map(cat => ({
    category: labels[cat] ?? cat,
    current:  currentData[cat]  ?? 0,
    last:     lastWeekData[cat] ?? 0,
  }))

  return (
    <div style={s.wrap} role="img" aria-label={ariaLabel ?? 'Radar chart'}>
      <ResponsiveContainer width="100%" height={300}>
        <RechartsRadar data={chartData}>
          <PolarGrid stroke={COLORS.gridLine} />
          <PolarAngleAxis
            dataKey="category"
            tick={{ fontSize: 13, fontWeight: 600, fill: COLORS.textPrimary, fontFamily: 'Nunito, sans-serif' }}
          />
          <PolarRadiusAxis
            angle={90}
            domain={[0, 100]}
            tick={{ fontSize: 11, fill: COLORS.textSecondary }}
            tickCount={4}
          />
          <Radar
            name={currentLabel}
            dataKey="current"
            stroke={COLORS.green}
            fill={COLORS.green}
            fillOpacity={0.25}
            strokeWidth={2}
          />
          {hasLastWeek && (
            <Radar
              name={lastLabel}
              dataKey="last"
              stroke={COLORS.textSecondary}
              fill={COLORS.textSecondary}
              fillOpacity={0.12}
              strokeWidth={1.5}
              strokeDasharray="4 3"
            />
          )}
          {hasLastWeek && (
            <Legend
              wrapperStyle={{ fontSize: 13, fontFamily: 'Nunito, sans-serif', paddingTop: 8 }}
            />
          )}
        </RechartsRadar>
      </ResponsiveContainer>
    </div>
  )
}

const s: Record<string, CSSProperties> = {
  wrap: {
    width:  '100%',
    margin: '0 auto',
  },
}
